import type { RouteLocationNormalized } from 'vue-router'
import type { Nullable } from '~/types'

/**
 * Matches Segment's PageContext shape and all properties are matched as close
 * as we can to Segment's own implementation:
 * https://github.com/segmentio/analytics-next/blob/2d6d5530180489c45675fe9537a42745df9a847e/packages/page-tools/src/page-context.ts#L130-L140.
 */
export interface SegmentPageContext {
  path: string
  referrer: string
  search: string
  title: string
  url: string
}

let current: Nullable<SegmentPageContext> = null
let currentRouteName: Nullable<string> = null
let currentReferrer = ''
let previousHref: Nullable<string> = null

/**
 * http://www-1.rvezy.dev#test -> http://www-1.rvezy.dev
 *
 * @param href The URL to parse
 */
function removeHash(href: string) {
  const hashIdx = href.indexOf('#')
  return hashIdx === -1 ? href : href.slice(0, hashIdx)
}

/**
 * Legacy Segment behavior: append search params unless the canonical already
 * has a query.
 *
 * @param canonicalUrl The URL
 * @param search The search query
 */
function createCanonicalURL(canonicalUrl: string, search: string) {
  return canonicalUrl.indexOf('?') > -1 ? canonicalUrl : canonicalUrl + search
}

/**
 * Segment's behavior: if the canonical is not a valid URL, treat it as a raw
 * path.
 *
 * @param canonicalUrl The URL
 */
function parseCanonicalPath(canonicalUrl: string) {
  try {
    return new URL(canonicalUrl).pathname
  }
  catch {
    return canonicalUrl[0] === '/' ? canonicalUrl : `/${canonicalUrl}`
  }
}

/**
 * Reads the actual canonical URL from the document.
 *
 * @returns The URL or undefined
 */
function readCanonical(): string | undefined {
  const canonical = document.querySelector('link[rel=\'canonical\']')
  return (canonical && canonical.getAttribute('href')) || undefined
}

/**
 * Equivalent of Segment's createPageContext(getDefaultBufferedPageContext()),
 * with our own referrer.
 *
 * @param canonicalUrl The URL
 */
function buildPageContext(canonicalUrl: string | undefined): SegmentPageContext {
  const search = location.search

  return {
    path: canonicalUrl ? parseCanonicalPath(canonicalUrl) : location.pathname,
    referrer: currentReferrer,
    search,
    title: document.title,
    url: canonicalUrl ? createCanonicalURL(canonicalUrl, search) : removeHash(location.href),
  }
}

/**
 * Called in router.afterEach. The browser URL is already updated at this point,
 * but <head> (title, canonical) is not, so the canonical is skipped
 * until finalizePageContext() runs.
 *
 * @param to The page the user came from
 * @param from The page the user navigated to
 */
export function setPageContextFromRoute(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
) {
  const isInitialLoad = from.matched.length === 0

  // Browsers never include the hash in a referrer, so we don't either.
  currentReferrer = isInitialLoad || !previousHref
    ? document.referrer
    : removeHash(previousHref)

  previousHref = location.href
  currentRouteName = to.name?.toString() ?? null

  current = {
    ...buildPageContext(undefined),
    // Prefer an explicit route title while the DOM title is still the previous page's
    title: (to.meta.title as string | undefined) ?? document.title,
  }
}

/**
 * Called after the head has rendered for the new page.
 * Re-reads title and canonical so the result matches what Segment would compute.
 */
export function finalizePageContext() {
  if (!current) {
    return
  }

  current = buildPageContext(readCanonical())
}

/**
 * Gets the page context to send to Segment.
 *
 * @returns The page context or null
 */
export function getPageContext(): Nullable<SegmentPageContext> {
  return current ? { ...current } : null
}

/**
 * gets the current route name.
 *
 * @returns The route name or null
 */
export function getPageRouteName(): Nullable<string> {
  return currentRouteName
}
